import React, { useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { Form, Row, Col, Card, Button, Alert } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import api from './../../../../api';
import { useSelector } from 'react-redux';
import { toastrAdd } from '../../../../actions/ToastrActions';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IoIosAlert } from 'react-icons/io';

import { partnerFetch } from "../../../../actions";

PersonForm.propTypes = {
    logo: PropTypes.object,
    setLogo: PropTypes.func,
    loading: PropTypes.bool,
    saving: PropTypes.bool,
    partner: PropTypes.object,
    onSubmit: PropTypes.func
};

function PersonForm(props) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { person, loading, saving, callback, personIsLinked } = props;
    const { event, partner } = useSelector(state => state);
    const partnerID = localStorage.getItem('partnerID');

    useEffect(() => {
        dispatch(partnerFetch(partnerID));
    }, [dispatch, partnerID]);

    const onSubmit = ({ emailMarketingAllowed, smsMarketingAllowed, ...rest }) => {
        const values = {
            ...rest,
            emails: [{
                email: rest.email,
                primary: true
            }],
            phones: rest.phone ? [{
                phone: rest.phone ,
                primary: true
            }] : [],
            event: {
                emailMarketingAllowed,
                smsMarketingAllowed,
                personType: 3,
                personStatus: 3,
                eventID: event.eventID,
                partnerID
            },
            company: partner.data.name && partner.data.name,
            organizationID: event.organizationID
        }
        api.person.updateEventPerson(event.eventID, values).then(response => {
            if (callback) {
                callback()
            }
            dispatch(toastrAdd({code: 0, details: t('persons.save_success_details'), title: t('persons.save_success_title'), type: 'success'}));
        }).catch(error => {
            dispatch(toastrAdd({code: 0, details: `${t('general.details')}: ${error.errors[0].details}`, title: t('errors.general_form_error_title'), type: 'error'}));
        })
    }

    let mappedPerson = {
        ...person,
        email: person.emails && person.emails.length ? person.emails[0].email : '',
        phone: person.phones && person.phones.length ? person.phones[0].phone : '',
        jobTitle: person.jobTitle && person.jobTitle.length ? person.jobTitle : '',
        description: person.description && person.description.length ? person.description : '',
        streetAddress: person.streetAddress && person.streetAddress.length ? person.streetAddress : '',
        postalCode: person.postalCode && person.postalCode.length ? person.postalCode : '',
        city: person.city && person.city.length ? person.city : '',
        emailMarketingAllowed: person.event && person.event.emailMarketingAllowed !== undefined ? person.event.emailMarketingAllowed : true,
        smsMarketingAllowed: person.event && person.event.smsMarketingAllowed !== undefined ? person.event.smsMarketingAllowed : true
    }

    /**
     * Formik configuration
     */
    const formik = useFormik({
        initialValues: {...mappedPerson},
        enableReinitialize: true,
        validationSchema: Yup.object().shape({
            firstNames: Yup.string()
                .required(t('form.first_name_required')),
            lastName: Yup.string()
                .required(t('form.last_name_required')),
            email: Yup.string()
                .required(t('form.email_required'))
                .email(t('form.email_invalid')),
            phone: Yup.string()
                .min(7, t('form.7_characters'))
                .nullable()
        }),
        onSubmit: values => {
            onSubmit(values)
        }
    });
    const { values, errors, touched } = formik;

    return (
        <Form onSubmit={formik.handleSubmit}>
            {personIsLinked ?
                <Alert variant="success" className="d-flex align-items-center">
                    <div>
                        <IoIosAlert size={30} style={{ marginRight: '10px' }} />
                    </div>
                    <div>
                        {t('event_personnel.person_creation_person_linked', { email: person.emails[0].email })}
                    </div>
                </Alert>
            : null}
            <Form.Group as={Row} controlId="firstNames" className="required">
                <Form.Label column sm={3}>{t('general.first_name')}</Form.Label>
                <Col sm={9}>
                    <Form.Control
                        value={values.firstNames || ''}
                        onChange={formik.handleChange}
                        disabled={saving}
                        isInvalid={errors.firstNames && touched.firstNames}
                        required
                    />
                    {errors.firstNames && touched.firstNames ? (
                        <Form.Control.Feedback type="invalid">
                            {errors.firstNames}
                        </Form.Control.Feedback>
                    ) : null}
                </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="lastName" className="required">
                <Form.Label column sm={3}>{t('general.last_name')}</Form.Label>
                <Col sm={9}>
                    <Form.Control
                        value={values.lastName || ''}
                        onChange={formik.handleChange}
                        disabled={saving}
                        isInvalid={errors.lastName && touched.lastName}
                        required
                    />
                    {errors.lastName && touched.lastName ? (
                        <Form.Control.Feedback type="invalid">
                            {errors.lastName}
                        </Form.Control.Feedback>
                    ) : null}
                </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="email" className="required">
                <Form.Label column sm={3}>{t('general.email')}</Form.Label>
                <Col sm={9}>
                    <Form.Control
                        value={values.email || ''}
                        onChange={formik.handleChange}
                        disabled={saving}
                        isInvalid={errors.email && touched.email}
                        required
                    />
                    {errors.email && touched.email ? (
                        <Form.Control.Feedback type="invalid">
                            {errors.email}
                        </Form.Control.Feedback>
                    ) : null}
                </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="phone">
                <Form.Label column sm={3}>{t('general.phone_number')}</Form.Label>
                <Col sm={9}>
                    <Form.Control
                        value={values.phone || ''}
                        onChange={formik.handleChange}
                        disabled={saving}
                        isInvalid={errors.phone && touched.phone}
                    />
                    {errors.phone && touched.phone ? (
                        <Form.Control.Feedback type="invalid">
                            {errors.phone}
                        </Form.Control.Feedback>
                    ) : null}
                </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="description">
                <Form.Label column sm={3}>{t('general.description')}</Form.Label>
                <Col sm={9}>
                    <Form.Control
                            value={values.description || ''}
                            onChange={formik.handleChange}
                            disabled={saving}
                    />
                </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="jobTitle">
                <Form.Label column sm={3}>{t('general.job_title')}</Form.Label>
                <Col sm={9}>
                    <Form.Control
                        value={values.jobTitle || ''}
                        onChange={formik.handleChange}
                        disabled={saving}
                    />
                </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="streetAddress">
                <Form.Label column sm={3}>{t('general.street_address')}</Form.Label>
                <Col sm={9}>
                    <Form.Control
                        value={values.streetAddress || ''}
                        onChange={formik.handleChange}
                        disabled={saving}
                    />
                </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="postalCode">
                <Form.Label column sm={3}>{t('general.postal_code')}</Form.Label>
                <Col sm={3}>
                    <Form.Control
                        value={values.postalCode || ''}
                        onChange={formik.handleChange}
                        disabled={saving}
                    />
                </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="city">
                <Form.Label column sm={3}>{t('general.city')}</Form.Label>
                <Col sm={6}>
                    <Form.Control
                        value={values.city || ''}
                        onChange={formik.handleChange}
                        disabled={saving}
                    />
                </Col>
            </Form.Group>
            <Form.Group as={Row}>
                <Form.Label column sm={3}>{t('Marketing channels')}</Form.Label>
                <Col sm={6}>
                    <Form.Check 
                        type="checkbox"
                        id="emailMarketingAllowed"
                        label={t('Email')}
                        onChange={formik.handleChange}
                        defaultChecked={values.emailMarketingAllowed}
                        value={values.emailMarketingAllowed}
                    />
                    <Form.Check 
                        type="checkbox"
                        id="smsMarketingAllowed"
                        label={t('SMS')}
                        onChange={formik.handleChange}
                        defaultChecked={values.smsMarketingAllowed}
                        value={values.smsMarketingAllowed}
                    />
                </Col>
            </Form.Group>
            <Card.Footer>
                <Form.Group as={Row}>
                    <Col sm={{span: 9, offset: 3}}>
                        <Button type="submit" variant="success" disabled={(loading || saving)}>
                            {person.personID ? t('general.save_changes') : t('general.save')}
                        </Button>
                    </Col>
                </Form.Group>
            </Card.Footer>
        </Form>
    )
}

export default PersonForm;